/** Single-flight-deduped loaders for Canvas config/detail resources that
 * are legitimately needed by both `CanvasBoard` (board-level, once) and
 * individual node components (per-node fallback, historically N times) —
 * see `canvasRequestDedup.ts` for the cache contract this builds on.
 *
 * VISTOERA_CANVAS_CAPACITY_CATEGORY_A_SCALING_FIX_AND_REBASELINE_V1
 * P0-C/P0-D. */

import {
  fetchAudioGenerationParameterCapability,
  getImagePickerItemDetail,
  getVideoGenConfig,
  getVideoGenEngines,
  getWorkspaceProviderOptions,
} from '../../../api'
import type {
  AudioGenerationParameterCapabilityResponse,
  ImagePickerDetail,
  VideoGenConfig,
  VideoGenEnginesResponse,
  WorkspaceProviderOptionsResponse,
} from '../../../api'
import { singleFlightCached } from './canvasRequestDedup'

// Config/engine catalogs change only when an operator edits provider
// setup — 60s is generous headroom for "board fetch + every node's
// fallback effect, all firing within the same render pass" to collapse
// into one request, while still bounding staleness to well under a
// minute if an Admin edits the config mid-session.
const CONFIG_TTL_MS = 60_000
// Asset detail (thumbnail/preview URLs, usage) can change from a
// same-session "更换图片" elsewhere, but that always mints a NEW entity
// id for the replaced reference, not a mutation of this id's own detail
// — 30s is intentionally shorter than the config TTL since asset detail
// is per-entity data, not shared provider config.
const ASSET_DETAIL_TTL_MS = 30_000

/** Canonical, single-flight-deduped `getVideoGenConfig` — call this from
 * every site that previously called `getVideoGenConfig` directly
 * (`CanvasBoard`'s own board-level fetch AND `GenerateVideoNode`'s
 * per-node fallback both go through here now), so N simultaneous callers
 * for the same workspace always collapse into exactly one request. */
export function loadVideoGenConfigDeduped(workspaceId: string): Promise<VideoGenConfig> {
  return singleFlightCached(`videoGenConfig:${workspaceId}`, () => getVideoGenConfig(workspaceId).then((r) => r.data), CONFIG_TTL_MS)
}

/** Canonical, single-flight-deduped `getVideoGenEngines`. */
export function loadVideoGenEnginesDeduped(workspaceId: string): Promise<VideoGenEnginesResponse> {
  return singleFlightCached(
    `videoGenEngines:${workspaceId}`,
    () => getVideoGenEngines(workspaceId).then((r) => r.data),
    CONFIG_TTL_MS,
  )
}

/** Canonical, single-flight-deduped `getImagePickerItemDetail` — every
 * node/material component that needs a given asset's thumbnail/preview
 * detail calls this instead of `getImagePickerItemDetail` directly, so M
 * nodes referencing the SAME asset id collapse into exactly one request
 * (previously M independent requests — measured 3x on a real Preview
 * project with one asset referenced by 3 components). Keyed on the full
 * parameter set (not just the id) so a future caller requesting a
 * different `include_usage`/`include_variants` shape never silently
 * reuses a differently-shaped cached response. */
export function loadImagePickerItemDetailDeduped(
  workspaceId: string,
  pickerItemId: string,
  params?: { include_usage?: boolean; include_variants?: boolean },
): Promise<ImagePickerDetail> {
  const includeUsage = params?.include_usage ?? false
  const includeVariants = params?.include_variants ?? false
  const key = `imagePickerItemDetail:${workspaceId}:${pickerItemId}:${includeUsage}:${includeVariants}`
  return singleFlightCached(key, () => getImagePickerItemDetail(workspaceId, pickerItemId, params).then((r) => r.data), ASSET_DETAIL_TTL_MS)
}

/** Capability-scoped Provider Options for Canvas Generate nodes.
 * N Try-On nodes on one board collapse into one request per
 * workspace+capability (TTL 60s). Never polls. */
export function loadAvailableProviderOptionsDeduped(
  workspaceId: string,
  capability: string,
  locale?: string,
): Promise<WorkspaceProviderOptionsResponse> {
  const localeKey = locale || ''
  const key = `workspaceProviderOptions:${workspaceId}:${capability}:${localeKey}`
  return singleFlightCached(
    key,
    () => getWorkspaceProviderOptions(workspaceId, capability, locale).then((r) => r.data),
    CONFIG_TTL_MS,
  )
}

/** VistoeraTASK-00217 — canonical Provider-Capability read for Pitch/
 * Speed on the Generate Audio node. Every `GenerateAudioNode` instance
 * on a board shares one workspace-scoped request (TTL 60s, same
 * rationale as `loadVideoGenConfigDeduped` above) instead of each node
 * independently polling the active Provider's real bounds. */
export function loadAudioGenerationParameterCapabilityDeduped(
  workspaceId: string,
): Promise<AudioGenerationParameterCapabilityResponse> {
  return singleFlightCached(
    `audioGenerationParameterCapability:${workspaceId}`,
    () => fetchAudioGenerationParameterCapability(workspaceId).then((r) => r.data),
    CONFIG_TTL_MS,
  )
}
